import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useOnClickOutside } from 'shared/hooks';
import { C } from 'shared/helpers';

import Styles from './Multiselect.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleDown,
  faAngleDoubleDown,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';

const Multiselect = ({
  selectKey,
  label,
  placeholder,
  stateKey,
  options,
  valueRef,
  setValueRef,
  openRef,
  setOpenRef,
  onChoice,
  disabled,
  style,
}) => {
  const { Select, Label, Box, List, Open, Disabled, Scroll, Selected } = Styles;

  const [lookup, setLookup] = useState(''); // Search string state
  const selectRef = useRef(); // Reference to outermost container
  const search = useRef(null); // Reference to search box
  const [openState, setOpenState] = useState(false); // Internal openState
  /** External value and setter function references */
  const [value, setValue] = [valueRef, setValueRef];

  /** Decide between using the internal open state or the external, if provided */
  const [open, setOpen] =
    openRef && setOpenRef ? [openRef, setOpenRef] : [openState, setOpenState];

  /** rLabel because the word label is taken below */
  const rLabel = label;

  /** Curb the provided options collection,
   *  so that the component is able to accept multiple options input formats.  */
  if (options && JSON.stringify(options) !== '[]') {
    while (
      options[0] instanceof Array &&
      !(
        options[0].length === 2 &&
        typeof options[0][0] === 'string' &&
        typeof options[0][1] === 'string'
      )
    ) {
      options = options[0];
    }
    if (
      options instanceof Array &&
      options[0] instanceof Array &&
      options[0][1]
    ) {
      options = options.map(option => ({
        label: option[1] || option[0],
        value: option[0],
      }));
    } else if (options instanceof Array && typeof options[0] === 'string') {
      options = options.map(option => ({
        label: option,
        value: option,
      }));
    }
  } else {
    options = [{ label: '', value: '' }];
  }

  /** Check if component is disabled.
   *  The prop may come as an external bool,
   *  or a function that instantly decides based on the component's state,
   *  and whatever else said function deals with from the external context. */
  disabled =
    typeof disabled === 'function'
      ? disabled({ options, value, label, placeholder })
      : disabled;

  /**
   * The Choose function calls the provided setter with the given value,
   * inserting it into the valueRef array or removing it depending on the value of insert.
   * insert is defined in the call made to this function down below, not provided externally.
   * The value of instant is always defined by
   * the presence (false) or absence (true) of value in the valueRef array.
   *
   * After that, choose calls whatever subscriber functions were provided
   * to the onChange event of this component (onChoice).
   * @param {array} value
   * @param {boolean} insert
   */
  const choose = (value, insert) => {
    if (insert) {
      setValue(
        stateKey
          ? state => ({ ...state, [stateKey]: [...valueRef, value] })
          : [...valueRef, value],
      );
      onChoice(value, valueRef, insert);
    } else {
      setValue(
        stateKey
          ? state => ({
              ...state,
              [stateKey]: valueRef.filter(x => x !== value),
            })
          : valueRef.filter(x => x !== value),
      );
      onChoice(value, valueRef, insert);
    }
  };

  /** Hook to dismiss (close) this component upon having a click outside of itself. */
  useOnClickOutside(selectRef, () => setOpen(false));

  return (
    <div
      ref={selectRef}
      style={{ ...style }}
      className={C(Select, [disabled, Disabled])}
    >
      <div className={Label} style={{ display: label ? 'block' : 'none' }}>
        {label}
      </div>
      <div
        onClick={
          !disabled
            ? () =>
                setOpen(o => !o) || (search.current && search.current.focus())
            : null
        }
        className={C(Box, [open, Open])}
      >
        {valueRef.length > 3
          ? `${valueRef.length} seleções`
          : valueRef.join(', ')}
        <span>
          <FontAwesomeIcon
            icon={open && !disabled ? faAngleDoubleDown : faAngleDown}
          />
        </span>
      </div>
      {!disabled && options && options.length && options[0].value && (
        <div className={C(List, [open && !disabled, Open])}>
          <div>
            <input
              value={lookup}
              onChange={({ target }) => setLookup(target.value)}
              ref={search}
            />
          </div>
          <div className={Scroll}>
            {options.map(({ label, value }, index) => (
              <div
                key={`${selectKey}_${(rLabel || '').normalize(
                  'NFD',
                )}_${label}_${value}_${++index}`}
                className={value === valueRef ? Selected : null}
                style={{
                  display:
                    !lookup ||
                    label
                      .toLocaleLowerCase()
                      .includes(lookup.toLocaleLowerCase())
                      ? 'flex'
                      : 'none',
                }}
                onClick={() => choose(value, !valueRef.includes(value))}
              >
                <p>
                  <FontAwesomeIcon
                    style={{
                      display: (valueRef || []).includes(value)
                        ? 'inline'
                        : 'none',
                      marginRight: '10px',
                    }}
                    icon={faCheck}
                  />
                  {label}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

Multiselect.defaultProps = {
  selectKey: '',
  label: '',
  placeholder: '',
  stateKey: '',
  valueRef: null,
  setValueRef: null,
  openRef: null,
  setOpenRef: null,
  onChoice: () => {},
  options: [],
  disabled: false,
  style: {},
};

Multiselect.propTypes = {
  style: PropTypes.object,
  selectKey: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  stateKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.any,
  valueRef: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  setValueRef: PropTypes.func,
  openRef: PropTypes.bool,
  setOpenRef: PropTypes.func,
  onChoice: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
};

export default Multiselect;
